import React from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import type { User } from '@supabase/supabase-js'
import { getUserProfilePicture } from '@/lib/utils'
import { LogOut } from 'lucide-react'

interface UserProfileCardProps {
  user: User
  isCollapsed?: boolean
}

export function UserProfileCard({ user, isCollapsed = false }: UserProfileCardProps) {
  const navigate = useNavigate()

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      navigate('/')
    } catch (error) {
      console.error('Error signing out:', error)
      toast.error("Error signing out")
    }
  }

  if (isCollapsed) {
    return (
      <div className="user-profile flex items-center justify-center p-3 rounded-xl bg-sidebar-accent/50 hover:bg-sidebar-accent/70 transition-colors duration-200">
        <img
          src={getUserProfilePicture(user)}
          alt="User avatar"
          className="w-8 h-8 rounded-full object-cover border border-sidebar-border/30"
          onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
        />
      </div>
    )
  }

  return (
    <div className="user-profile flex items-center gap-2 px-0 py-3 rounded-xl bg-sidebar-accent/50 hover:bg-sidebar-accent/70 transition-all duration-200 group">
      <img
        src={getUserProfilePicture(user)}
        alt="User avatar"
        className="w-8 h-8 rounded-full object-cover border border-sidebar-border/30 flex-shrink-0"
        onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
      />
      <div className="flex-1 min-w-0 mr-4">
        <div className="font-semibold text-sidebar-foreground text-sm leading-tight truncate">
          {user.user_metadata?.full_name || user.user_metadata?.name || 'User'}
        </div>
        <div className="text-xs text-muted-foreground truncate leading-tight">
          {user.email}
        </div>
      </div>
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-all duration-200 flex-shrink-0 group-hover:scale-105" 
        title="Sign Out" 
        onClick={handleSignOut}
      >
        <LogOut className="w-4 h-4" />
      </Button>
    </div>
  )
}
